import { addDays, addMonths, addWeeks, format } from 'date-fns';
import { IntlShape } from 'react-intl';
import { HH_MM, HH_MM_DD_MM_YYYY, ONE_MINUTE_IN_MILLISECONDS, YYYY_MM_DD } from '../data-types/dates/datesConstants';
import { EventItem } from '../data-types/sports/sportsInterfaces';
import { DateTimeframe } from '../data-types/timing/timingInterfaces';
import { SportsbookEventsState } from '../state/types/sportsbookEventsType';
import { getDateString } from './calculations';
import { getKeysFromEventsMap } from './sportsbook';

export const formatEventDate = (eventDateTime: string): string => {
  const eventDateFormatted = new Date(eventDateTime);
  const isToday = isTodayDate(eventDateFormatted);

  return format(eventDateFormatted, isToday ? HH_MM : HH_MM_DD_MM_YYYY);
};

export const subtractYearsFromDate = (years: number, date = new Date()) => new Date(date.setUTCFullYear(date.getUTCFullYear() - years));

export const convertSecondsToMilliseconds = (seconds: number): number => seconds * 1000;

export const addDaysToDate = (days: number, date = new Date()) => addDays(date, days);

export const addWeeksToDate = (weeks: number, date = new Date()) => addWeeks(date, weeks);

export const addMonthsToDate = (months: number, date = new Date()) => addMonths(date, months);

export const getMillisecondsDifference = (startDate: Date, endDate: Date) => endDate.getTime() - startDate.getTime();

export const getDateDifferenceTimeframe = (startDate: Date, endDate: Date): DateTimeframe => {
  const milliseconds = getMillisecondsDifference(startDate, endDate);

  let seconds = Math.floor(milliseconds / 1000);
  let minutes = Math.floor(milliseconds / 60 / 1000);
  let hours = Math.floor(milliseconds / 60 / 60 / 1000);
  const days = Math.floor(milliseconds / 24 / 60 / 60 / 1000);

  hours %= 24;
  minutes %= 60;
  seconds %= 60;

  return {
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
  };
};

export const isSecondDateAfterFirst = (date1: Date, date2: Date) => date1 < date2;

export const isDateInTheNextNMinutes = (startDate: Date, targetMinutes: number) => {
  const timeFrame = getDateDifferenceTimeframe(new Date(), startDate);
  const { days, hours, minutes } = timeFrame;

  return days === 0 && hours === 0 && minutes < targetMinutes;
};

export const formatToDateString = (date: string, intl: IntlShape) => {
  const dateString = getDateString(date);
  if (dateString.id) {
    return intl.formatMessage({ id: dateString.id }).toUpperCase();
  }

  return dateString.date || date;
};

export const tryToGetDateFromString = (date: string) => {
  try {
    return new Date(date);
  } catch (e) {
    return undefined;
  }
};

export const getAllUniqueDatesFromEvents = (events: EventItem[], intl: IntlShape) =>
  events.map(event => formatToDateString(event.startDate, intl)).filter((item, index, self) => self.indexOf(item) === index);

export const getAllUniqueDatesFromEventsMap = (events: SportsbookEventsState, intl: IntlShape) => {
  const dates: string[] = [];

  getKeysFromEventsMap(events).forEach(key => {
    const event = events[key];
    const eventDate = formatToDateString(event.startDate, intl);
    if (!dates.includes(eventDate)) {
      dates.push(eventDate);
    }
  });

  return dates;
};

export const isTodayDate = (date: Date) => {
  const today = format(new Date(), YYYY_MM_DD);
  const dateFormatted = format(date, YYYY_MM_DD);

  return today === dateFormatted;
};

export const getDateWithTimezoneOffset = (date: Date) => {
  const localizedTimestamp = date.getTime() + date.getTimezoneOffset() * ONE_MINUTE_IN_MILLISECONDS;
  return new Date(localizedTimestamp);
};
